import update from 'immutability-helper';
import _ from 'lodash';
import { Color, PaperScope, Point, Raster, Size } from 'paper/dist/paper-core';
import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { Unit, units } from 'src/components/widgets/areaDesigner/helpers';
import LayersUploadDialog from 'src/components/widgets/areaDesigner/layersUploadDialog';
import MdFormatAlignCenter from 'src/shared/icons/align-center-solid.svg';
import MdCopyAll from 'src/shared/icons/copy-solid.svg';
import MinusIcon from 'src/shared/icons/minus-circle-solid.svg';
import PlusIcon from 'src/shared/icons/plus-circle-solid.svg';
import MdClear from 'src/shared/icons/times-solid.svg';
import { Mutable } from 'src/shared/types';
import styled from 'styled-components';
import DocumentIcon from '../../../shared/icons/border-none-solid.svg';
import PlaceholderIcon from '../../../shared/icons/border-style-solid.svg';
import FontIcon from '../../../shared/icons/font-solid.svg';
import MdOutlineAdd from '../../../shared/icons/plus-solid.svg';
import QRIcon from '../../../shared/icons/qrcode-solid.svg';
import UploadIcon from '../../../shared/icons/upload-solid.svg';
// import CutMarksIcon from '../../../shared/icons/table-solid.svg';
import { Input } from '../../widgets/input';
import { PrimaryButton } from '../../widgets/buttons';
import { QuestionDialog, useDialogManager } from '../../widgets/dialog';
import { showErrorNotification } from '../../widgets/notificationToaster';
import { SidebarButton } from '../../widgets/layouts';
import { TextArea } from '../../widgets/textArea';
import { openFileDialog, T, uuid } from 'src/shared/helpers';
import { useSubscriptionData } from 'src/shared/hooks/useSubscriptionData';
import EyeSlashIcon from '../../../shared/icons/eye-slash-solid.svg';
import EyeIcon from '../../../shared/icons/eye-solid.svg';
import TagSlashIcon from '../../../shared/icons/tags-slash-solid.svg';
import TagIcon from '../../../shared/icons/tags-solid.svg';
import { productFragment_model$data } from '../productConfiguration/state/queries/__generated__/productFragment_model.graphql';
import { placementQuery$data } from './__generated__/placementQuery.graphql';
import { PlacementAreaDesignerViewer } from './placementAreaDesignerViewer';
import { PlacementTreeSidebar } from './placementSideBar';
import barcode128Preview from './shared/images/code-128.png';
import qrcodePreview from './shared/images/qrcode.png';
import {
    BulkForm,
    BulkMenu,
    InfoAreaItem,
    InfoAreaMenu,
    ItemType,
    MenuIconButton,
    MenuPages,
    ModelAreaMenu,
    PlaceholderMenu,
    PlacementOutputFileType,
    PlacementTool,
    ppcm,
    QrBarCodeMenu,
    QrBarCodeType,
    SettingsMenu,
    TextMenu,
    UploadPdfMenu
} from './shared/sharedPlacementComponents';
import { PlacementInput } from './utils';
export interface Area {
    areaID: number;
    sideID: number;
    colorID: number;
    placementOutputAreaItemID?: number | null;
    guid: string;
    path: string;
    pathSVG: string;
    name: string;
    completeName?: string;
    clipOut: boolean;
    boundsX: number;
    boundsY: number;
    boundsWidth: number;
    boundsHeight: number;
    originalBoundsWidth?: number;
    originalBoundsHeight?: number;
    // layers: Layer[];
    templateFile: TemplateFile | null;
    ppcm?: number;
    rotation: number;
    cloned?: boolean;
    visible?: boolean;
    labelVisibility?: boolean;
}
export interface PlacementItem {
    placementOutputItemID?: number | null;

    guid: string;
    path: string;
    pathSVG: string;
    name?: string;

    boundsX: number;
    boundsY: number;
    boundsWidth: number;
    boundsHeight: number;
    rotation: number;
    scaling: number;
    itemType: ItemType;

    color?: string;
    fontSize?: number;
    fontFamily?: string;
    textAlignment?: 'left' | 'center' | 'right';
    content?: string;
    imgUrl?: string;
    isPlaceholder?: boolean;
    visible?: boolean;
    labelVisibility?: boolean;
}

export const isArea = (value: Area | PlacementItem): value is Area => {
    return (value as Area).areaID !== undefined;
};

export const isPlacementItem = (value: Area | PlacementItem): value is PlacementItem => {
    return (value as PlacementItem).itemType !== undefined || (value as PlacementItem).isPlaceholder !== undefined;
};

export interface PlacementLayer {
    id?: number | null;
    colorID: number;
    fileID?: string;
    jsonArea: string;
    guid?: string;
    fileObjectID?: number | null;
}

export interface Layer {
    id: string;
    json: string;
    svg: string;
    isClipMask: boolean;
}

export interface TemplateFile {
    documentWidth: number;
    documentHeight: number;
}

export interface PlacementSettings {
    placementOutputFileType: PlacementOutputFileType;
}

const AreaDesignerLayout = styled.div`
    display: grid;
    grid-template-columns: 300px auto 300px;
    user-select: none;
    overflow: hidden;
    height: 100%;
`;

const AreaDesignerViewerContainer = styled.div`
    width: 100%;
    height: 100%;
    overflow: hidden;
    background-color: #f3f5f6;
`;

const AreaDesignerSidePanel = styled.div`
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    flex: 1;
`;

const LayerContent = styled.div`
    overflow: hidden;
`;

const Layer = styled.li<{ opened: boolean }>`
    border-radius: 3px;
    display: flex;
    flex-direction: column;
    font-size: 12px;

    ${Input}, ${TextArea} {
        font-size: 12px;
        padding: 5px;
        margin-bottom: 5px;
        height: auto;
    }

    ${LayerContent} {
        height: ${(props) => (props.opened ? 'auto' : '0px')};
        padding: ${(props) => (props.opened ? '15px 0 15px 30px' : '0')};
    }
`;

interface AreaDesignerSettings {
    unit: (typeof units)[number]['value'];
}

const _PanelContainer = styled.div`
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    padding: 0;
    flex: 1;
    margin: 0;
`;

const RigthPanelContainer = styled(_PanelContainer)`
    padding-left: 16px;
`;

const LeftPanelContainer = styled(_PanelContainer)`
    padding-right: 16px;
`;

const PanelContent = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    overflow-y: auto;
    font-size: 13px;
`;

const RelativePanelContent = styled.div`
    position: relative;
    bottom: 60px;
`;

const PanelSubContent = styled.div`
    display: flex;
    flex-direction: column;
    padding: 15px;
`;

const PanelFooter = styled.div`
    display: flex;
    justify-content: center;
    padding: 15px;
    flex-direction: column;
`;

const getStartingUnit = () => {
    let unit: Unit = units[1];

    try {
        const settings: AreaDesignerSettings | null = localStorage.getItem('area_designer_settings')
            ? JSON.parse(localStorage.getItem('area_designer_settings')!)
            : null;
        if (settings) {
            const newUnit = units.find((x) => x.value === settings.unit);

            if (newUnit) unit = newUnit;
        }
    } catch {}

    return unit;
};

const IconButtonMenuContainer = styled.div`
    display: flex;
    justify-content: center;
    gap: 10px;
    min-width: 0;
`;

export interface PlacementAreaDesigner {
    savingInProgress: boolean;
    onSaveClick: (placement: PlacementInput) => void;
    model: productFragment_model$data;
    placement: placementQuery$data | null;
}

export interface SidebarPlacementItem<T extends string> {
    id: T;
    label: string;
    subItems?: SidebarPlacementItem<T>[];
    areaID?: number;
    sideID?: number;
    colorID?: number;
    areas: Area[];
    added?: boolean;
    buttons?: SidebarButton[];
    selected?: boolean;
}

export const PlacementAreaDesigner: FC<PlacementAreaDesigner> = ({ model, placement, savingInProgress, onSaveClick }) => {
    const { showDialog, closeDialog } = useDialogManager();

    const viewerRef = useRef<{ resize: () => void }>(null);
    const [selectedItemGuid, setSelectedItemGuid] = useState<string | null | undefined>(null);
    const [selectedItemsGuid, setSelectedItemsGuid] = useState<string[]>([]);
    const [unit, setUnit] = useState<Unit>(getStartingUnit());
    const [tool, setTool] = useState<PlacementTool>('pan');
    const [currentMenuPage, setCurrentMenuPage] = useState<MenuPages>(MenuPages.None);

    const [changing, setChanging] = useState<boolean>(false);

    //useful to force some input component
    const [forceInfoAreaUpdate, setForceInfoAreaUpdate] = useState(false);

    const colors = model.colors;

    const [areasInPlacement, setAreasInPlacement] = useState<Area[]>([]);
    const [itemsInPlacement, setItemsInPlacement] = useState<PlacementItem[]>([]);
    const [layersInPlacement, setLayersInPlacement] = useState<PlacementLayer[]>([]);
    const [settingsInPlacement, setSettingsInPlacement] = useState<PlacementSettings | null>(null);

    //user for bulk option
    const [selectedSides, setSelectedSides] = useState<number[]>([]);
    const [selectAllVariants, setSelectAllVariants] = useState<boolean>(false);

    const [name, setName] = useState<string>(placement?.placementOutput?.name ?? 'Advanced print-ready files');
    const [dpi, setDpi] = useState<number>(placement?.placementOutput?.dpi ?? 300);
    const [widthCM, setWidthCM] = useState<number>(placement?.placementOutput?.widthCM ?? 100);
    const [heightCM, setHeightCM] = useState<number>(placement?.placementOutput?.heightCM ?? 100);

    const [stacked, setStacked] = useState(
        placement?.placementOutput?.settings
            ? placement?.placementOutput?.settings?.placementOutputFileType === 'ONE_FILE_FOR_ALL_DESIGN'
                ? true
                : false
            : true
    );

    const [bulkSection, setBulkSection] = useState<boolean>(false);
    const { subscription, updateSubscription } = useSubscriptionData();

    useEffect(() => {
        (async () => {
            const savedRectangles = placement?.placementOutput?.placementOutputAreaItems;
            const paperScope = new PaperScope();
            paperScope.setup(new Size(1, 1));

            if (savedRectangles) {
                const areas = savedRectangles.map((savedRectangle) => {
                    const svgSavedRectangle = savedRectangle.svg;
                    const rectanglePath = paperScope.project.activeLayer.importSVG(svgSavedRectangle, {
                        expandShapes: true
                    });

                    rectanglePath.strokeColor = new paperScope.Color(1, 1, 0, 1);
                    rectanglePath.strokeWidth = 2;
                    rectanglePath.fillColor = new paperScope.Color(1, 1, 0, 1);

                    type AreaType = Mutable<(typeof colors)[0]['sides'][0]['areas'][0]>;
                    let area = colors
                        .find((x) => x.colorID === savedRectangle!.colorID)!
                        .sides.find((x) => x.sideID === savedRectangle!.sideID)!
                        .areas.find((x) => x.areaID === savedRectangle!.areaID) as AreaType;

                    let modelArea = paperScope.project.activeLayer.importJSON(area.path);

                    // parse the svg to get the rotation
                    const parser = new DOMParser();
                    const doc = parser.parseFromString(svgSavedRectangle, 'image/svg+xml');
                    const dataRotation = doc.documentElement.getAttribute('data-rotation');
                    const angle = dataRotation ? parseFloat(dataRotation) : 0;

                    // Remap the rectangle position to document origin
                    const placementWidthPX = (placement?.placementOutput?.widthCM ?? 0) * 100;
                    const placementHeightPX = (placement?.placementOutput?.heightCM ?? 0) * 100;
                    const rectanglePosition = rectanglePath.position.subtract(new paperScope.Point(placementWidthPX / 2, placementHeightPX / 2));
                    modelArea.position = rectanglePosition;

                    modelArea.scale(rectanglePath.bounds.width / modelArea.bounds.width);
                    modelArea.rotate(angle);
                    modelArea.data.rotation = angle;
                    modelArea.data.itemName = getCompleteItemName(savedRectangle?.colorID, savedRectangle?.sideID, savedRectangle?.areaID);

                    let areaGuid = uuid();
                    let visible = true;
                    let labelVisibility = true;

                    modelArea.data.guid = areaGuid;

                    try {
                        // areaGuid = JSON.parse(savedRectangle.json)?.[1].data.guid;
                        visible = JSON.parse(savedRectangle.json)?.[1].data.visible;
                        labelVisibility = JSON.parse(savedRectangle.json)?.[1].data.labelVisibility;
                    } catch (e) {
                        console.error(e);
                    }

                    return {
                        ...area,
                        pathSVG: modelArea.exportSVG(),
                        path: modelArea.exportJSON(),
                        areaID: savedRectangle?.areaID,
                        colorID: savedRectangle?.colorID,
                        sideID: savedRectangle?.sideID,
                        placementOutputAreaItemID: savedRectangle?.placementOutputAreaItemID,
                        guid: areaGuid ? areaGuid : uuid(),
                        boundsX: modelArea.bounds.x,
                        boundsY: modelArea.bounds.y,
                        rotation: angle,
                        completeName: modelArea.data.itemName,
                        visible: visible,
                        labelVisibility: labelVisibility
                    } as Area;
                });
                setAreasInPlacement(areas);
            }

            const savedLayers = placement?.placementOutput?.placementOutputLayers;
            let placementLayer = savedLayers?.map((savedLayer) => {
                return {
                    id: savedLayer.placementOutputLayerID,
                    colorID: savedLayer.colorID,
                    fileID: savedLayer.fileID,
                    jsonArea: savedLayer.jsonArea,
                    fileObjectID: savedLayer.fileObjectID
                } as PlacementLayer;
            });

            if (placement?.placementOutput?.templateFileObjectID || placement?.placementOutput?.templateFileID) {
                if (!placementLayer) placementLayer = [];

                //concat the general pdf
                placementLayer = placementLayer.concat([
                    {
                        fileObjectID: placement?.placementOutput?.templateFileObjectID,
                        colorID: -1,
                        fileID: placement?.placementOutput?.templateFileID,
                        jsonArea: placement?.placementOutput?.jsonArea ?? '{}'
                    }
                ]);
            }

            setLayersInPlacement(placementLayer ?? []);

            const savedItems = placement?.placementOutput?.placementOutputItems ?? [];

            const placementItems = await Promise.all(
                savedItems.map(async (savedItem) => {
                    const jsonItem = JSON.parse(savedItem.json);

                    let paperItem = paperScope.project.activeLayer.importJSON(jsonItem.paperJson);

                    paperItem = await new Promise<any>((resolve) => {
                        if (paperItem instanceof Raster) {
                            paperItem.onLoad = () => {
                                resolve(paperItem);
                            };
                        } else {
                            resolve(paperItem);
                        }
                    });

                    return {
                        placementOutputItemID: savedItem.placementOutputItemID,
                        guid: jsonItem.guid,
                        path: jsonItem.paperJson,
                        pathSVG: '',
                        name: jsonItem.name,

                        boundsX: paperItem.bounds.x,
                        boundsY: paperItem.bounds.y,
                        boundsWidth: paperItem.bounds.width,
                        boundsHeight: paperItem.bounds.height,
                        rotation: jsonItem.rotation,
                        scaling: jsonItem.scaling,

                        color: jsonItem.color,
                        fontSize: jsonItem.fontSize,
                        textAlignment: jsonItem.justification,
                        content: jsonItem.content,
                        itemType: Object.values(ItemType)[jsonItem.itemType],
                        imgUrl: jsonItem.imgUrl,
                        isPlaceholder: jsonItem.isPlaceholder
                    } as PlacementItem;
                })
            );

            setItemsInPlacement(placementItems ?? []);
        })();
    }, [placement?.placementOutput]);

    useEffect(() => {
        const selectedItem = [...areasInPlacement, ...itemsInPlacement].find((x) => x.guid === selectedItemGuid);

        if (selectedItemsGuid.length > 1) {
            setCurrentMenuPage(MenuPages.InfoArea);
        } else if (selectedItem) {
            // setSelectedItemGuid(selectedItem?.guid);

            //area and placeholder
            if (selectedItem && (isArea(selectedItem) || (isPlacementItem(selectedItem) && selectedItem.isPlaceholder))) {
                setCurrentMenuPage(MenuPages.InfoArea);
            }
            //qr e bar code
            else if (
                selectedItem &&
                isPlacementItem(selectedItem) &&
                (selectedItem.itemType === ItemType.QRCODE || selectedItem.itemType === ItemType.BARCODE128)
            ) {
                setCurrentMenuPage(MenuPages.InfoArea);
            }
            //text
            else if (selectedItem && isPlacementItem(selectedItem) && selectedItem.itemType === ItemType.TEXT && !selectedItem.isPlaceholder) {
                setCurrentMenuPage(MenuPages.Text);
            }
        }
        // else {
        //     setCurrentMenuPage(MenuPages.None);
        //     // setSelectedItemGuid(null);
        // }
    }, [selectedItemsGuid, areasInPlacement, itemsInPlacement]);

    // used to keep updated the bulk option checkbox
    useEffect(() => {
        if (areasInPlacement.length === 0) {
            setSelectedSides([]);
            setSelectAllVariants(false);
            return;
        }

        const variants = colors as Mutable<productFragment_model$data['colors']>;

        const modelAreas = variants.flatMap((variant) =>
            variant.sides.flatMap((side) =>
                side.areas.map((area) => ({
                    ...area,
                    sideID: side.sideID,
                    colorID: variant.colorID,
                    ppcm: side.ppcm
                }))
            )
        );

        const modelAreasCounter = modelAreas.reduce((acc, area) => {
            acc.set(area.sideID, (acc.get(area.sideID) || 0) + 1);
            return acc;
        }, new Map());

        const areasInPlacementCounter = areasInPlacement
            .filter((x) => !x.cloned)
            .reduce((acc, area) => {
                acc.set(area.sideID, (acc.get(area.sideID) || 0) + 1);
                return acc;
            }, new Map());

        let newSelectedSides = [...selectedSides];

        areasInPlacementCounter.forEach((value, key) => {
            if (modelAreasCounter.get(key) === value && !selectedSides.includes(key)) {
                newSelectedSides = update(newSelectedSides, { $push: [key] });
            } else if (modelAreasCounter.get(key) != value && selectedSides.includes(key)) {
                const index = selectedSides.indexOf(key);
                newSelectedSides = update(selectedSides, { $splice: [[index, 1]] });
            }
        });

        setSelectedSides(newSelectedSides);

        const allSelected = [...modelAreasCounter].every(([key, value]) => areasInPlacementCounter.has(key) && areasInPlacementCounter.get(key) === value);
        setSelectAllVariants(allSelected);
    }, [areasInPlacement]);

    // useEffect(() => {
    //     //  !selectedItemsGuid.includes(selectedItemGuid))
    //     if(selectedItemGuid && selectedItemsGuid.length ===  0)
    //         setSelectedItemsGuid([selectedItemGuid]);
    // }, [selectedItemGuid])

    useEffect(() => {
        const newSettings: PlacementSettings = {
            placementOutputFileType: stacked ? 'ONE_FILE_FOR_ALL_DESIGN' : 'ONE_FILE_FOR_EACH_DESIGN'
        };

        setSettingsInPlacement((prev) =>
            update(prev, {
                $set: newSettings
            })
        );
    }, [stacked]);

    const handleAreaRemoved = (area: Area | PlacementItem) => {
        setAreasInPlacement([...areasInPlacement.filter((x) => x.guid != area.guid)]);
        setItemsInPlacement([...itemsInPlacement.filter((x) => x.guid != area.guid)]);
        setCurrentMenuPage(MenuPages.None);
    };

    const handleItemCreated = (item: PlacementItem) => {
        setItemsInPlacement([...itemsInPlacement, item]);
    };

    const convertToOriginX = (value: number | undefined) => {
        return !value ? '0' : (value + (widthCM * ppcm) / 2).toFixed(2);
    };

    const convertPositionXToRatio = (value: number | undefined) => {
        return !value ? 0 : (parseFloat(convertToOriginX(value)) / (ppcm * unit.ratio)).toFixed(2);
    };

    const convertToOriginY = (value: number | undefined) => {
        return !value ? '0' : (value + (heightCM * ppcm) / 2).toFixed(2);
    };

    const convertPositionYToRatio = (value: number | undefined) => {
        return !value ? 0 : (parseFloat(convertToOriginY(value)) / (ppcm * unit.ratio)).toFixed(2);
    };

    const toRatio = (value: number | undefined) => {
        return !value ? 0 : (value / (ppcm * unit.ratio)).toFixed(2);
    };

    const handleItemChanged = (items: (Area | PlacementItem)[], newPPCM: number | null, layers: Layer[]) => {
        for (const item of items) {
            if (isArea(item)) {
                const index = areasInPlacement.findIndex((x) => x.guid === item.guid);
                if (index >= 0) {
                    setAreasInPlacement((prev) =>
                        update(prev, {
                            [index]: {
                                $set: item as Area
                            }
                        })
                    );
                }
            } else if (isPlacementItem(item)) {
                const index = itemsInPlacement.findIndex((x) => x.guid === item.guid);
                if (index >= 0) {
                    setItemsInPlacement((prev) =>
                        update(prev, {
                            [index]: {
                                $set: item
                            }
                        })
                    );
                }
            }
        }

        setForceInfoAreaUpdate(!forceInfoAreaUpdate);
    };

    const handleChangeItemRotation = async (guid: string, value: number) => {
        let index = areasInPlacement.findIndex((x) => x.guid === guid);
        let itemJsonResult;
        if (index >= 0) {
            const paperScope = new PaperScope();
            paperScope.setup(new Size(1, 1));
            const areaJSON = paperScope.project.activeLayer.importJSON(areasInPlacement[index].path);

            const delta = value - (areasInPlacement[index].rotation ?? 0);

            areaJSON.rotate(delta);
            areaJSON.data.rotation = areaJSON.rotation;

            let newArea = _.clone(areasInPlacement[index]);
            newArea.pathSVG = areaJSON.exportSVG({ asString: true }) as string;
            newArea.path = areaJSON.exportJSON();

            newArea.boundsX = areaJSON.bounds.x;
            newArea.boundsY = areaJSON.bounds.y;

            newArea.boundsWidth = areaJSON.bounds.width;
            newArea.boundsHeight = areaJSON.bounds.height;

            if (!newArea.rotation) newArea.rotation = 0;

            newArea.rotation += delta;

            setAreasInPlacement(
                update(areasInPlacement, {
                    [index]: {
                        $set: newArea
                    }
                })
            );
        } else {
            index = itemsInPlacement.findIndex((x) => x.guid === guid);

            if (index >= 0) {
                const paperScope = new PaperScope();
                paperScope.setup(new Size(1, 1));
                let itemJSON = paperScope.project.activeLayer.importJSON(itemsInPlacement[index].path);

                itemJSON = await new Promise<any>((resolve) => {
                    if (itemJSON instanceof Raster) {
                        itemJSON.onLoad = () => {
                            resolve(itemJSON);
                        };
                    } else {
                        resolve(itemJSON);
                    }
                });

                const delta = value - (itemsInPlacement[index].rotation ?? 0);
                itemJSON.rotate(delta);
                itemJSON.data.rotation = itemJSON.rotation;

                let newItem = _.clone(itemsInPlacement[index]);
                newItem.pathSVG = itemJSON.exportSVG({ asString: true }) as string;
                newItem.path = itemJSON.exportJSON();

                newItem.boundsX = itemJSON.bounds.x;
                newItem.boundsY = itemJSON.bounds.y;

                newItem.boundsWidth = itemJSON.bounds.width;
                newItem.boundsHeight = itemJSON.bounds.height;

                if (!newItem.rotation) newItem.rotation = 0;

                newItem.rotation += delta;

                itemJsonResult = {
                    w: toRatio(newItem.boundsWidth),
                    h: toRatio(newItem.boundsHeight),
                    x: convertPositionXToRatio(newItem.boundsX),
                    y: convertPositionXToRatio(newItem.boundsY)
                };

                setItemsInPlacement(
                    update(itemsInPlacement, {
                        [index]: {
                            $set: newItem
                        }
                    })
                );
            }
        }
        return itemJsonResult;
    };

    const handleChangeItemPosition = async (guid: string, value: number, axis: string) => {
        const paperScope = new PaperScope();
        paperScope.setup(new Size(1, 1));

        let index = areasInPlacement.findIndex((x) => x.guid === guid);

        if (index >= 0) {
            const areaJSON = paperScope.project.activeLayer.importJSON(areasInPlacement[index].path);

            if (axis === 'x') areaJSON.position.x = value * ppcm * unit.ratio - (widthCM * ppcm) / 2 + areaJSON.bounds.width / 2;
            else areaJSON.position.y = value * ppcm * unit.ratio - (heightCM * ppcm) / 2 + areaJSON.bounds.height / 2;

            let newArea = _.clone(areasInPlacement[index]);
            newArea.pathSVG = areaJSON.exportSVG({ asString: true }) as string;
            newArea.path = areaJSON.exportJSON();

            newArea.boundsX = areaJSON.bounds.x;
            newArea.boundsY = areaJSON.bounds.y;

            newArea.boundsWidth = areaJSON.bounds.width;
            newArea.boundsHeight = areaJSON.bounds.height;

            setAreasInPlacement(
                update(areasInPlacement, {
                    [index]: {
                        $set: newArea
                    }
                })
            );
        } else {
            index = itemsInPlacement.findIndex((x) => x.guid === guid);

            if (index >= 0) {
                if (!changing) {
                    setChanging(true);

                    let itemJSON = paperScope.project.activeLayer.importJSON(itemsInPlacement[index].path);

                    itemJSON = await new Promise<any>((resolve) => {
                        if (itemJSON instanceof Raster) {
                            itemJSON.onLoad = () => {
                                setChanging(false);
                                resolve(itemJSON);
                            };
                        } else {
                            setChanging(false);
                            resolve(itemJSON);
                        }
                    });

                    if (axis === 'x') itemJSON.position.x = value * ppcm * unit.ratio - (widthCM * ppcm) / 2 + itemJSON.bounds.width / 2;
                    else itemJSON.position.y = value * ppcm * unit.ratio - (heightCM * ppcm) / 2 + itemJSON.bounds.height / 2;

                    let newItem = _.clone(itemsInPlacement[index]);
                    newItem.pathSVG = itemJSON.exportSVG({ asString: true }) as string;
                    newItem.path = itemJSON.exportJSON();

                    newItem.boundsX = itemJSON.bounds.x;
                    newItem.boundsY = itemJSON.bounds.y;

                    newItem.boundsWidth = itemJSON.bounds.width;
                    newItem.boundsHeight = itemJSON.bounds.height;

                    setItemsInPlacement(
                        update(itemsInPlacement, {
                            [index]: {
                                $set: newItem
                            }
                        })
                    );
                }
            }
        }
    };

    const handleChangeItemSize = async (guid: string, value: number, sizeType: string) => {
        const paperScope = new PaperScope();
        paperScope.setup(new Size(1, 1));

        let index = areasInPlacement.findIndex((x) => x.guid === guid);
        let itemJsonResult;
        if (index >= 0) {
            // a.cresci non puo' ridimensionare l'area da tastiera
        } else {
            index = itemsInPlacement.findIndex((x) => x.guid === guid);
            if (index >= 0) {
                if (!changing) {
                    setChanging(true);

                    let itemJSON = paperScope.project.activeLayer.importJSON(itemsInPlacement[index].path);

                    itemJSON = await new Promise<any>((resolve) => {
                        if (itemJSON instanceof Raster) {
                            itemJSON.onLoad = () => {
                                setChanging(false);
                                resolve(itemJSON);
                            };
                        } else {
                            setChanging(false);
                            resolve(itemJSON);
                        }
                    });
                    let scaleX = 1;
                    let scaleY = 1;

                    if (sizeType === 'width' && value > 0) {
                        scaleX = (value * ppcm * unit.ratio) / itemJSON.bounds.width;
                        itemJSON.scale(scaleX, itemJSON.bounds.topLeft);
                    }
                    if (sizeType === 'height' && value > 0) {
                        scaleY = (value * ppcm * unit.ratio) / itemJSON.bounds.height;
                        itemJSON.scale(scaleY, itemJSON.bounds.topLeft);
                    }

                    let newItem = _.clone(itemsInPlacement[index]);
                    newItem.pathSVG = itemJSON.exportSVG({ asString: true }) as string;
                    newItem.path = itemJSON.exportJSON();

                    newItem.boundsX = itemJSON.bounds.x;
                    newItem.boundsY = itemJSON.bounds.y;

                    newItem.boundsWidth = itemJSON.bounds.width;
                    newItem.boundsHeight = itemJSON.bounds.height;

                    newItem.scaling = itemJSON.scaling.y;
                    if (newItem.fontSize) {
                        newItem.fontSize *= newItem.scaling;
                    }

                    itemJsonResult = {
                        w: toRatio(newItem.boundsWidth),
                        h: toRatio(newItem.boundsHeight),
                        x: convertPositionXToRatio(newItem.boundsX),
                        y: convertPositionXToRatio(newItem.boundsY)
                    };

                    setItemsInPlacement(
                        update(itemsInPlacement, {
                            [index]: {
                                $set: newItem
                            }
                        })
                    );
                }
            }
        }
        return itemJsonResult;
        // this.updateHandles(0, this.selectedPaperItem.bounds.center, true);
    };

    const handleText = (value: string, fontFamily: string, textColor: string, textAlignment: 'left' | 'center' | 'right') => {
        const paperScope = new PaperScope();
        paperScope.setup(new Size(1, 1));

        let fontSize = (heightCM * ppcm) / 20;

        const index = itemsInPlacement.findIndex((x) => x.guid === selectedItemGuid);

        let text = index !== -1 ? (paperScope.project.activeLayer.importJSON(itemsInPlacement[index].path) as any) : new paperScope.PointText(new Point(0, 0));

        if (value === '' && index !== -1 && !text.data.isPlaceholder) {
            setItemsInPlacement([...itemsInPlacement.filter((x) => x.guid !== itemsInPlacement[index]?.guid)]);
            setSelectedItemGuid(null); //TODO impostare uno useEffect
            setSelectedItemsGuid([]);
            return;
        } else if (value === '') {
            return;
        }

        if (index !== -1) {
            fontSize = text.fontSize as number;
        }

        text.justification = 'center';
        text.fillColor = new Color(textColor);
        text.strokeColor = new Color(textColor);
        text.content = value;
        text.fontSize = fontSize;
        text.data.isText = true;
        text.fontFamily = fontFamily;
        text.justification = textAlignment;

        if (index < 0) {
            const targetHeight = (heightCM * ppcm) / 20;
            text.scale(targetHeight / text.bounds.height, targetHeight / text.bounds.height);
        }
        let fontSizeScaled = text.fontSize;
        if (index !== -1) {
            fontSizeScaled = text.fontSize * text.scaling.x;
        }

        let item = {
            name: value,
            pathSVG: text.exportSVG(),
            path: text.exportJSON(),
            guid: itemsInPlacement[index]?.guid ?? uuid(),
            boundsX: text.bounds.x,
            boundsY: text.bounds.y,
            boundsWidth: text.bounds.width,
            boundsHeight: text.bounds.height,
            color: text.fillColor.toCSS(true),
            fontSize: fontSizeScaled,
            fontFamily: text.fontFamily,
            textAlignment: textAlignment,
            content: value,
            rotation: text.rotation,
            scaling: text.scaling.x,
            itemType: ItemType.TEXT
        } as PlacementItem;

        if (index !== -1) {
            setItemsInPlacement(
                update(itemsInPlacement, {
                    [index]: {
                        $set: item
                    }
                })
            );
        } else {
            setItemsInPlacement([...itemsInPlacement, item]);
        }

        setSelectedItemGuid(item.guid);
        if (selectedItemsGuid.length === 0) setSelectedItemsGuid([item.guid]);
    };

    const handleMoveAreasToCenter = () => {
        const toMoveAreas = areasInPlacement.filter((area) => selectedItemsGuid.includes(area.guid));
        for (const area of toMoveAreas) {
            const index = areasInPlacement.findIndex((x) => x.guid === area.guid);
            if (index >= 0) {
                setAreasInPlacement((prev) =>
                    update(prev, {
                        [index]: {
                            $set: moveTo(area) as Area
                        }
                    })
                );
            }
        }

        const toMoveItems = itemsInPlacement.filter((item) => selectedItemsGuid.includes(item.guid));
        for (const item of toMoveItems) {
            const index = itemsInPlacement.findIndex((x) => x.guid === item.guid);
            if (index >= 0) {
                setItemsInPlacement((prev) =>
                    update(prev, {
                        [index]: {
                            $set: moveTo(item) as PlacementItem
                        }
                    })
                );
            }
        }

        const allNewGuid = [...toMoveAreas, ...toMoveItems].map((x) => x.guid);
        setSelectedItemsGuid(allNewGuid);
    };

    const handleDuplicateItems = () => {
        const proportionalValue = 10;

        const toDuplicateAreas = areasInPlacement.filter((area) => selectedItemsGuid.includes(area.guid));

        let duplicateAreas = toDuplicateAreas.map((toDuplicateArea) =>
            moveTo(
                {
                    ...toDuplicateArea,
                    placementOutputAreaItemID: null,
                    guid: uuid(),
                    cloned: true
                } as Area,
                (widthCM / proportionalValue) * dpi,
                (heightCM / proportionalValue) * dpi
            )
        ) as Area[];

        setAreasInPlacement([...areasInPlacement, ...duplicateAreas]);

        const toDuplicateItems = itemsInPlacement.filter((item) => selectedItemsGuid.includes(item.guid));

        let duplicateItems = toDuplicateItems.map((toDuplicateItem) => {
            let deltaY = toDuplicateItem.boundsHeight;
            // const deltaX = (widthCM / proportionalValue) * dpi;
            // const deltaY = (heightCM / proportionalValue) * dpi;

            const objDuplicated = {
                ...toDuplicateItem,
                placementOutputAreaItemID: null,
                guid: uuid(),
                cloned: true
            } as PlacementItem;

            if (objDuplicated.hasOwnProperty('placementOutputItemID')) {
                delete objDuplicated.placementOutputItemID;
            }
            const itemDuplicated = moveTo(objDuplicated, 25, deltaY);
            return itemDuplicated;
        }) as PlacementItem[];

        const allNewGuid = [...duplicateAreas, ...duplicateItems].map((x) => x.guid);
        setSelectedItemsGuid(allNewGuid);
        setItemsInPlacement([...itemsInPlacement, ...duplicateItems]);
    };

    const handleOpenTextPage = () => {
        setSelectedItemGuid(null);
        setSelectedItemsGuid([]);
        setCurrentMenuPage(MenuPages.Text);
    };

    const handlePlaceholders = (value: string) => {
        //TODO manca il fontFamily

        const paperScope = new PaperScope();
        paperScope.setup(new Size(1, 1));

        const fontFamily = 'Adamina';

        var text = new paperScope.PointText(new Point(0, 0));
        text.justification = 'center';
        text.fillColor = new Color('#000000');
        text.content = value;
        text.fontSize = (heightCM * ppcm) / 20; //initial font (pt) 1/20 of the document height
        text.data.isPlaceholder = true;
        text.data.rotation = 0;
        text.fontFamily = fontFamily;

        let item = {
            name: value,
            pathSVG: text.exportSVG(),
            path: text.exportJSON(),
            guid: uuid(),
            boundsX: text.bounds.x,
            boundsY: text.bounds.y,
            boundsWidth: text.bounds.width,
            boundsHeight: text.bounds.height,
            color: text.fillColor.toCSS(true),
            fontSize: text.fontSize,
            fontFamily: fontFamily,
            content: value,
            isPlaceholder: true,
            itemType: ItemType.TEXT,
            rotation: 0
        } as PlacementItem;

        setItemsInPlacement([...itemsInPlacement, item]);

        setSelectedItemGuid(item.guid);
        setSelectedItemsGuid([item.guid]);
    };

    const handleRemovePdf = (colorID?: number) => {
        if (colorID) {
            showDialog(
                'removePdf',
                <QuestionDialog
                    message={T._('Do you want remove the layer?', 'Admin')}
                    onYesClick={async () => {
                        closeDialog('removePdf');
                        setLayersInPlacement([...layersInPlacement.filter((x) => x.colorID != colorID)]);
                    }}
                />
            );
        }
    };

    const createRasterItem = (url: string) => {
        return new Promise<any>((resolve) => {
            var item = new Raster({
                crossOrigin: 'anonymous',
                source: url,
                position: new Point(0, 0)
            });

            item.onLoad = () => {
                resolve(item);
            };
        });
    };

    const handleConfirmAddQrBarCode = async (type: QrBarCodeType, data: string, containsPlaceholder: boolean) => {
        const paperScope = new PaperScope();
        paperScope.setup(new Size(1, 1));

        let placeholderImage;
        let itemType: ItemType;

        switch (type) {
            case 'qr':
                placeholderImage = qrcodePreview;
                itemType = ItemType.QRCODE;
                break;
            case 'code-128':
                placeholderImage = barcode128Preview;
                itemType = ItemType.BARCODE128;
                break;
        }

        createRasterItem(placeholderImage).then((newImageItem) => {
            // 1/10 of document height
            newImageItem.scale((heightCM * ppcm) / 10 / newImageItem.bounds.width);

            newImageItem.applyMatrix = true;
            newImageItem.data.isPlaceholder = true;
            newImageItem.data.rotation = 0;

            let placementItem = {
                guid: uuid(),
                pathSVG: newImageItem.exportSVG(),
                path: newImageItem.exportJSON(),
                boundsX: newImageItem.bounds.x,
                boundsY: newImageItem.bounds.y,
                boundsWidth: newImageItem.bounds.width,
                boundsHeight: newImageItem.bounds.height,
                rotation: 0,
                isPlaceholder: containsPlaceholder,

                content: data,
                itemType: itemType
            } as PlacementItem;

            setItemsInPlacement([...itemsInPlacement, placementItem]);

            // TODO - alla creazione non li seleziono perchè non vengono caricate le img.. fixare
            // setSelectedItemGuid(placementItem.guid);
            // setSelectedItemsGuid([placementItem.guid]);
        });
    };

    const handleQrCode = () => {
        setSelectedItemGuid(null);
        setCurrentMenuPage(MenuPages.QrBarCode);
    };

    const handleUploadPdf = (colorID?: number) => {
        openFileDialog(
            async (files) => {
                if (files.length > 0) {
                    const fileID = uuid();

                    showDialog(
                        'upload-pdf',
                        <LayersUploadDialog
                            fileID={fileID}
                            file={files[0]}
                            onComplete={(areaTemplateInfo) => {
                                closeDialog('upload-pdf');

                                if (areaTemplateInfo.layers.length === 0) {
                                    showErrorNotification({
                                        message: T._('The PDF must contain at least one layer.', 'Admin')
                                    });

                                    return;
                                }

                                if (Math.abs(areaTemplateInfo.sizeCm.width - widthCM) > 0.01 || Math.abs(areaTemplateInfo.sizeCm.height - heightCM) > 0.01) {
                                    showErrorNotification({
                                        message: T._('The size of the pdf uploaded are not compliant with the placement size.', 'Admin')
                                    });

                                    return;
                                }

                                const paperScope = new PaperScope();
                                paperScope.setup(new Size(1, 1));

                                const layerItems = areaTemplateInfo.layers.map((l) => paperScope.project.importSVG(l.svg));

                                const pdfLayerItem = new paperScope.Group(layerItems);

                                pdfLayerItem.scale((widthCM * ppcm) / pdfLayerItem.bounds.width, (heightCM * ppcm) / pdfLayerItem.bounds.height);

                                const newLayer: PlacementLayer = {
                                    id: null,
                                    colorID: colorID ?? -1,
                                    fileID: fileID,
                                    jsonArea: pdfLayerItem.exportJSON(),
                                    guid: uuid()
                                };
                                setLayersInPlacement([...layersInPlacement, newLayer]);
                            }}
                        />
                    );
                }
            },
            '.pdf',
            false
        );
    };

    const moveTo = (area: Area | PlacementItem, offsetX?: number, offsetY?: number) => {
        const paperScope = new PaperScope();
        paperScope.setup(new Size(1, 1));

        const areaJSON = paperScope.project.activeLayer.importJSON(area.path);

        areaJSON.position.x = offsetX ? areaJSON.position.x + offsetX : 0;
        areaJSON.position.y = offsetY ? areaJSON.position.y + offsetY : 0;

        let newArea = _.clone(area);
        newArea.pathSVG = areaJSON.exportSVG({ asString: true }) as string;
        newArea.path = areaJSON.exportJSON();

        newArea.boundsX = areaJSON.bounds.x;
        newArea.boundsY = areaJSON.bounds.y;

        newArea.boundsWidth = areaJSON.bounds.width;
        newArea.boundsHeight = areaJSON.bounds.height;

        return newArea;
    };

    const adaptToCurrentPpcm = (area: Area) => {
        const paperScope = new PaperScope();
        paperScope.setup(new Size(1, 1));

        const areaSvg = paperScope.project.activeLayer.importJSON(area.path);

        const ratio = area.ppcm ? ppcm / area.ppcm : 1;

        areaSvg.scale(ratio);

        let newArea = _.clone(area);

        newArea.pathSVG = areaSvg.exportSVG({ asString: true }) as string;
        newArea.path = areaSvg.exportJSON();

        newArea.boundsX = ratio * areaSvg.bounds.x;
        newArea.boundsY = ratio * areaSvg.bounds.y;

        newArea.boundsWidth = ratio * areaSvg.bounds.width;
        newArea.boundsHeight = ratio * areaSvg.bounds.height;

        return newArea;
    };

    const onSidebarItemClick = (item: SidebarPlacementItem<string>) => {
        //select all areas contains in the placement
        setSelectedItemsGuid(item.areas.filter((item) => areasInPlacement.some((item) => item.guid === item.guid)).map((x) => x.guid));
    };

    const calculateArea = (area: Area) => area.boundsWidth * area.boundsHeight;

    const sortItemByArea = (items: Area[]) => {
        return items.sort((a, b) => calculateArea(b) - calculateArea(a));
    };

    const onSidebarItemButtonClick = (button: SidebarButton, item: SidebarPlacementItem<string>) => {
        const [action, onType] = button.key.split('-');
        const areas = item.areas;

        switch (action) {
            case 'add':
                let newAreas = [] as Area[];

                const allAreasPresent = areas.every((area) =>
                    areasInPlacement.some((x) => (x.areaID === area.areaID && x.sideID === area.sideID && x.colorID === area.colorID) || x.guid === area.guid)
                );

                // if all areas (as parameters) area has already added on placement, will be removed
                if (allAreasPresent) {
                    newAreas = areasInPlacement.filter(
                        (area) =>
                            areas.findIndex(
                                (x) => (x.areaID === area.areaID && x.sideID === area.sideID && x.colorID === area.colorID) || x.guid === area.guid
                            ) === -1
                    );
                } else {
                    // otherwise, there are a few to add
                    newAreas = item.areas
                        .filter(
                            (area) =>
                                areasInPlacement.findIndex(
                                    (x) => (x.areaID === area.areaID && x.sideID === area.sideID && x.colorID === area.colorID) || x.guid === area.guid
                                ) === -1
                        )
                        .map((area) => moveTo(adaptToCurrentPpcm(area)) as Area);
                    newAreas = [...areasInPlacement, ...newAreas];
                }

                setAreasInPlacement([...sortItemByArea(newAreas)]);
                setSelectedItemsGuid([...areas.map((x) => x.guid)]);
                break;
            case 'visibility':
                let isSomeNotVisible = false;

                if (onType === 'variant' || onType === 'side') {
                    //prendo tutte le aree del canvas che stanno anche nella porzione di albero cliccato
                    const commonAreas = areasInPlacement.filter((area) => {
                        return areas.some(
                            (areaPlacement) =>
                                areaPlacement.colorID === area.colorID && areaPlacement.sideID === area.sideID && areaPlacement.areaID === area.areaID
                        );
                    });

                    isSomeNotVisible = commonAreas.every((x) => x.visible !== undefined && !x.visible);
                }

                const newAreasWithVisibility = areasInPlacement.map((area) => {
                    let visibility = area.visible === undefined ? false : !area.visible;

                    if (onType === 'variant' || onType === 'side') {
                        visibility = isSomeNotVisible;
                    }

                    const areaFounded = areas.find((x) => x.areaID === area.areaID && x.sideID === area.sideID && x.colorID === area.colorID);
                    if (areaFounded) {
                        return { ...area, visible: visibility };
                    }
                    return area;
                });

                setAreasInPlacement(newAreasWithVisibility);
                setSelectedItemsGuid([]);
                setSelectedItemGuid(null);
                break;
            case 'label':
                let isSomeLabelNotVisible = false;

                if (onType === 'variant' || onType === 'side') {
                    //prendo tutte le aree del canvas che stanno anche nella porzione di albero cliccato
                    const commonAreas = areasInPlacement.filter((area) => {
                        return areas.some(
                            (areaPlacement) =>
                                areaPlacement.colorID === area.colorID && areaPlacement.sideID === area.sideID && areaPlacement.areaID === area.areaID
                        );
                    });

                    isSomeLabelNotVisible = commonAreas.every((x) => x.labelVisibility !== undefined && !x.labelVisibility);
                }

                const modifiedAreas = areasInPlacement.map((area) => {
                    let labelVisibility = area.labelVisibility === undefined ? false : !area.labelVisibility;

                    if (onType === 'variant' || onType === 'side') {
                        labelVisibility = isSomeLabelNotVisible;
                    }

                    const areaFounded = areas.find((x) => x.areaID === area.areaID && x.sideID === area.sideID && x.colorID === area.colorID);
                    if (areaFounded) {
                        return { ...area, labelVisibility: labelVisibility };
                    }
                    return area;
                });

                setAreasInPlacement(modifiedAreas);
                break;
            default:
                console.error('Action not valid');
                break;
        }
    };

    const handleSelectAll = (value: boolean) => {
        setSelectAllVariants(value);

        const variants = colors as Mutable<productFragment_model$data['colors']>;

        if (value) {
            const modelAreas = variants.flatMap((variant) =>
                variant.sides.flatMap((side) =>
                    side.areas
                        .filter(
                            (area) =>
                                !areasInPlacement.some(
                                    (areaInPlacement) =>
                                        areaInPlacement.guid === area.guid ||
                                        (areaInPlacement.areaID === area.areaID &&
                                            areaInPlacement.sideID === side.sideID &&
                                            areaInPlacement.colorID === variant.colorID)
                                )
                        )
                        .map((area) => ({
                            ...area,
                            areaID: area.areaID,
                            sideID: side.sideID,
                            colorID: variant.colorID,
                            ppcm: side.ppcm,
                            rotation: 0
                        }))
                )
            );

            const newAreas: Area[] = modelAreas.map((area) => moveTo(adaptToCurrentPpcm(area)) as Area);
            setAreasInPlacement([...areasInPlacement, ...newAreas]);
        } else setAreasInPlacement([]);
    };
    const handleDeleteAllareas = () => {
        showDialog(
            'deleteAllAreas',
            <QuestionDialog
                message={T._('Do you want remove all areas"?', 'Admin')}
                onYesClick={async () => {
                    closeDialog('deleteAllAreas');
                    setAreasInPlacement([]);
                }}
            />
        );
    };

    const handleMultipleSelections = () => {
        setTool('multipleSelection');
    };

    const getInfoItems = () => {
        const items = [...areasInPlacement, ...itemsInPlacement].filter((x) => selectedItemsGuid.includes(x.guid));
        const infoItems = items.map((item) => {
            return {
                guid: item.guid,
                itemName: item?.name,
                variantName: 'colorID' in item && item.colorID !== 1 ? colors.find((color) => color.colorID === (item as Area).colorID)?.name : '',
                sideName:
                    'colorID' in item && 'sideID' in item
                        ? colors.find((color) => color.colorID === (item as Area).colorID)?.sides.find((side) => side.sideID === (item as Area).sideID)?.name
                        : '',
                size: { w: toRatio(item.boundsWidth), h: toRatio(item.boundsHeight) },
                position: { x: convertPositionXToRatio(item.boundsX), y: convertPositionYToRatio(item.boundsY) },
                rotation: item.rotation,
                unit: unit.value,
                handleChangeItemPosition: handleChangeItemPosition,
                handleChangeItemRotation: handleChangeItemRotation,
                handleChangeItemSize: handleChangeItemSize
            } as InfoAreaItem;
        });

        return infoItems;
    };

    const getCompleteItemName = (colorID: number, sideID: number, areaID: number) => {
        const variantName = colorID === 1 ? '' : colors.find((color) => color.colorID === colorID)?.name;
        const sideName = colors.find((color) => color.colorID === colorID)?.sides.find((side) => side.sideID === sideID)?.name;
        const areaName = colors
            .find((color) => color.colorID === colorID)
            ?.sides.find((side) => side.sideID === sideID)
            ?.areas.find((area) => area.areaID === areaID)?.name;

        const completeName = (areaName ? areaName + ', ' : '') + (sideName ? sideName : '') + (variantName ? ', ' + variantName : '');

        return completeName;
    };

    const handleDocumentSize = () => {
        setSelectedItemsGuid([]);
        setSelectedItemGuid(null);
        setCurrentMenuPage(MenuPages.Settings);
    };

    const handleSelectedSide = (sideID: number) => {
        const index = selectedSides.indexOf(sideID);

        let newSelectedSides: number[] = [];

        if (index === -1) {
            newSelectedSides = update(selectedSides, { $push: [sideID] });
            const variants = colors as Mutable<productFragment_model$data['colors']>;
            const modelAreas = variants.flatMap((variant) =>
                variant.sides
                    .filter((side) => newSelectedSides.includes(side.sideID))
                    .flatMap((side) =>
                        side.areas
                            .filter(
                                (area) =>
                                    !areasInPlacement.some(
                                        (areaInPlacement) =>
                                            areaInPlacement.guid === area.guid ||
                                            (areaInPlacement.areaID === area.areaID &&
                                                areaInPlacement.sideID === side.sideID &&
                                                areaInPlacement.colorID === variant.colorID)
                                    )
                            )
                            .map((area) => ({
                                ...area,
                                areaID: area.areaID,
                                sideID: side.sideID,
                                colorID: variant.colorID,
                                rotation: 0,
                                ppcm: side.ppcm,
                                completeName: getCompleteItemName(variant.colorID, side.sideID, area.areaID)
                            }))
                    )
            );

            const newAreas: Area[] = modelAreas.map((area) => moveTo(adaptToCurrentPpcm(area)) as Area);

            const allAreas = [...sortItemByArea([...areasInPlacement, ...newAreas])];
            setAreasInPlacement(allAreas);
            setSelectedItemsGuid(newAreas.map((x) => x.guid));
        } else {
            newSelectedSides = update(selectedSides, { $splice: [[index, 1]] });
            setAreasInPlacement(areasInPlacement.filter((area) => area.sideID != sideID));
        }

        setSelectedSides(newSelectedSides);
    };

    const distinctSides: { sideID: number; name: string }[] = useMemo(() => {
        return colors
            .flatMap((color) => color.sides)
            .filter((item, index, self) => self.findIndex((side) => side.sideID === item.sideID) === index)
            .map((side) => {
                return { sideID: side.sideID, name: side.name };
            });
    }, [colors]);

    const items: SidebarPlacementItem<string>[] = useMemo(() => {
        const variants = colors as Mutable<productFragment_model$data['colors']>;

        const modelAreas = variants.flatMap((variant) =>
            variant.sides.flatMap((side) =>
                side.areas.map((area) => ({
                    sideID: side.sideID,
                    colorID: variant.colorID,
                    ppcm: side.ppcm,
                    ...area
                }))
            )
        );

        const allAreas: Area[] = modelAreas.map((x) => ({
            ...x,
            ppcm: x.ppcm,
            rotation: 0,
            originalBoundsWidth: 0,
            originalBoundsHeight: 0,
            completeName: getCompleteItemName(x.colorID, x.sideID, x.areaID)
        }));

        return colors.map((color) => {
            const sides = color.sides?.map((side) => {
                const items = side.areas?.map((area, index) => {
                    const areaFounded = areasInPlacement.find((x) => x.areaID === area.areaID && x.sideID === side.sideID && x.colorID === color.colorID);
                    const isAreaActive = areaFounded ? true : false;
                    const isAreaVisible = areaFounded?.visible ?? true;
                    const isLabelEnabled = areaFounded?.labelVisibility === undefined || areaFounded?.labelVisibility;
                    const isAreaSelected = areaFounded ? selectedItemsGuid.includes(areaFounded?.guid) : false;

                    return {
                        id: (color.colorID + '-' + side.sideID + '-' + area.areaID).toString(),
                        areaID: area.areaID,
                        sideID: side.sideID,
                        colorID: color.colorID,
                        label: !area.name ? T._('Area ', 'Admin') + (index + 1).toString() : area.name,
                        areas: allAreas
                            ?.filter((a) => a.colorID === color.colorID)
                            .filter((a) => a.sideID === side.sideID)
                            .filter((a) => a.areaID === area.areaID),
                        added: isAreaActive,
                        selected: isAreaSelected,
                        buttons: [
                            { key: 'add-area', icon: isAreaActive ? <MinusIcon /> : <PlusIcon /> },
                            { key: 'visibility-area', icon: isAreaVisible ? <EyeIcon /> : <EyeSlashIcon /> },
                            { key: 'label-area', icon: isLabelEnabled ? <TagIcon /> : <TagSlashIcon /> }
                        ]
                    };
                });

                const isSideActive =
                    areasInPlacement?.filter((area) => area.colorID === color.colorID).filter((area) => area.sideID === side.sideID).length ===
                    modelAreas?.filter((area) => area.colorID === color.colorID).filter((area) => area.sideID === side.sideID).length;

                const sideAreas = areasInPlacement?.filter((area) => area.colorID === color.colorID).filter((area) => area.sideID === side.sideID);

                const isSideAreasVisible = sideAreas.length === 0 || sideAreas.some((area) => area.visible || area.visible === undefined);
                const isSideLabelEnabled = sideAreas.length === 0 || sideAreas.some((area) => area.labelVisibility === undefined || area.labelVisibility);

                return {
                    id: (color.colorID + '-' + side.sideID).toString(),
                    label: side.name,
                    areas: allAreas?.filter((area) => area.colorID === color.colorID).filter((area) => area.sideID === side.sideID),
                    subItems: items,
                    added: isSideActive,
                    buttons: [
                        { key: 'add-side', icon: isSideActive ? <MinusIcon /> : <PlusIcon /> },
                        { key: 'visibility-side', icon: isSideAreasVisible ? <EyeIcon /> : <EyeSlashIcon /> },
                        { key: 'label-side', icon: isSideLabelEnabled ? <TagIcon /> : <TagSlashIcon /> }
                    ]
                };
            });

            let isColorActive =
                areasInPlacement?.filter((area) => area.colorID === color.colorID).length ===
                modelAreas?.filter((area) => area.colorID === color.colorID).length;

            const colorAreas = areasInPlacement?.filter((area) => area.colorID === color.colorID);
            const isColorAreasVisible = colorAreas.length === 0 || colorAreas.some((area) => area.visible || area.visible === undefined);
            const isColorLabelEnabled = colorAreas.length === 0 || colorAreas.some((area) => area.labelVisibility === undefined || area.labelVisibility);

            return {
                id: color.colorID.toString(),
                label: color.colorID === 1 ? model.name : color.name,
                areas: allAreas?.filter((area) => area.colorID === color.colorID),
                subItems: sides,
                added: isColorActive,
                buttons: [
                    { key: 'add-variant', icon: isColorActive ? <MinusIcon /> : <PlusIcon /> },
                    { key: 'visibility-variant', icon: isColorAreasVisible ? <EyeIcon /> : <EyeSlashIcon /> },
                    { key: 'label-variant', icon: isColorLabelEnabled ? <TagIcon /> : <TagSlashIcon /> }
                ]
            };
        });
    }, [areasInPlacement]);

    return (
        <AreaDesignerLayout>
            <AreaDesignerSidePanel>
                <LeftPanelContainer>
                    <PanelContent>
                        {!bulkSection && (
                            <ModelAreaMenu rightIcon={{ icon: <MdOutlineAdd />, onRightIconClick: () => setBulkSection(!bulkSection) }}>
                                <PlacementTreeSidebar
                                    sidebarItems={items}
                                    onSidebarItemClick={onSidebarItemClick}
                                    onSidebarItemButtonClick={onSidebarItemButtonClick}
                                />
                            </ModelAreaMenu>
                        )}
                        {bulkSection && (
                            <>
                                <BulkMenu onBackButtonClick={() => setBulkSection(false)}>
                                    <BulkForm
                                        sides={distinctSides}
                                        selectedSides={selectedSides}
                                        onSelectedSide={handleSelectedSide}
                                        onSelectAll={handleSelectAll}
                                        selectedAll={selectAllVariants}
                                    />
                                </BulkMenu>
                            </>
                        )}
                    </PanelContent>
                </LeftPanelContainer>
            </AreaDesignerSidePanel>

            {/* <AreaDesignerViewerContainer ref={viewerContainerRef}> */}
            <PanelContent>
                <AreaDesignerViewerContainer>
                    <PlacementAreaDesignerViewer
                        ref={viewerRef}
                        unit={unit}
                        selectedItemGuid={selectedItemGuid}
                        selectedItemsGuid={selectedItemsGuid}
                        areas={areasInPlacement}
                        items={itemsInPlacement}
                        layers={layersInPlacement}
                        ppcm={ppcm}
                        tool={tool}
                        documentSize={{ widthCm: widthCM, heightCm: heightCM }}
                        onItemsChanged={handleItemChanged}
                        onAreaRemoved={handleAreaRemoved}
                        // onItemSelected={(item, isMultipleSelection) => {

                        //     if (!item?.guid)
                        //         setSelectedItemsGuid([]);

                        //     setSelectedItemGuid(item?.guid);

                        //     if (!isMultipleSelection && item?.guid && !selectedItemsGuid?.includes(item.guid))
                        //         setSelectedItemsGuid([item.guid]);

                        //     if (isMultipleSelection && item?.guid && !selectedItemsGuid?.includes(item.guid)) {
                        //         setSelectedItemsGuid([...selectedItemsGuid ?? [], item.guid ?? []]);
                        //     }
                        // }}
                        onItemsSelected={(items, referenceItemGuid) => {
                            const filteredItems = items?.filter((x) => x.visible === undefined || x.visible);

                            const isMultipleSelection = filteredItems ? filteredItems.length > 1 : false;

                            if (!filteredItems) {
                                setSelectedItemGuid(null);
                                setSelectedItemsGuid([]);
                                return;
                            }

                            if (filteredItems?.length === 1) {
                                // setSelectedItemGuid(items[0].guid);
                                // setSelectedItemsGuid([items[0].guid]);
                                setSelectedItemGuid(referenceItemGuid);
                                setSelectedItemsGuid([referenceItemGuid ?? filteredItems[0].guid]);
                            }

                            // if (!isMultipleSelection && item?.guid && !selectedItemsGuid?.includes(item.guid))
                            //     setSelectedItemsGuid([...items.map(x =>x.guid)]);

                            if (isMultipleSelection) {
                                setSelectedItemGuid(referenceItemGuid ?? filteredItems[0].guid);
                                setSelectedItemsGuid([...filteredItems.map((x) => x.guid)]);
                            }
                        }}
                    />

                    <RelativePanelContent>
                        <IconButtonMenuContainer>
                            {/* TODO creare un componente */}
                            {selectedItemGuid && (
                                <MenuIconButton customStyle={{ background: '#F3F5F6' }} icon={<MdCopyAll />} onClick={handleDuplicateItems}>
                                    {T._('Duplicate', 'Admin')}
                                </MenuIconButton>
                            )}
                            {selectedItemsGuid.length > 0 && (
                                <MenuIconButton customStyle={{ background: '#F3F5F6' }} icon={<MdFormatAlignCenter />} onClick={handleMoveAreasToCenter}>
                                    {T._('Align Center', 'Admin')}
                                </MenuIconButton>
                            )}
                            {areasInPlacement.length > 1 && (
                                <MenuIconButton customStyle={{ background: '#F3F5F6' }} icon={<MdClear />} onClick={handleDeleteAllareas}>
                                    {T._('Remove all areas', 'Admin')}
                                </MenuIconButton>
                            )}
                        </IconButtonMenuContainer>
                    </RelativePanelContent>
                </AreaDesignerViewerContainer>

                <PanelSubContent>
                    <IconButtonMenuContainer>
                        <MenuIconButton icon={<DocumentIcon />} onClick={handleDocumentSize}>
                            {T._('Size & Settings', 'Admin')}
                        </MenuIconButton>
                        <MenuIconButton icon={<PlaceholderIcon />} onClick={() => setCurrentMenuPage(MenuPages.Placeholder)}>
                            {T._('Placeholder', 'Admin')}
                        </MenuIconButton>
                        <MenuIconButton icon={<FontIcon />} onClick={handleOpenTextPage}>
                            {T._('Text', 'Admin')}
                        </MenuIconButton>
                        {/* <MenuIconButton icon={<CutMarksIcon />} onClick={handleCutMarks}>{T._('Cut marks', 'Admin')}</MenuIconButton> */}
                        <MenuIconButton icon={<QRIcon />} onClick={handleQrCode}>
                            {T._('QR/Barcode', 'Admin')}
                        </MenuIconButton>
                        <MenuIconButton icon={<UploadIcon />} onClick={() => setCurrentMenuPage(MenuPages.UploadPdf)}>
                            {T._('Upload PDF', 'Admin')}
                        </MenuIconButton>
                    </IconButtonMenuContainer>
                </PanelSubContent>
            </PanelContent>

            <AreaDesignerSidePanel>
                <RigthPanelContainer>
                    <PanelContent>
                        {currentMenuPage === MenuPages.None && <IconButtonMenuContainer />}

                        {currentMenuPage === MenuPages.Text && (
                            <TextMenu
                                key={selectedItemGuid}
                                item={itemsInPlacement.filter((x) => x.itemType === ItemType.TEXT && !x.isPlaceholder).find((x) => x.guid === selectedItemGuid)}
                                handleAreaChanged={handleText}
                            />
                        )}

                        {currentMenuPage === MenuPages.Placeholder && <PlaceholderMenu onButtonClick={handlePlaceholders} />}
                        {currentMenuPage === MenuPages.UploadPdf && (
                            <UploadPdfMenu
                                variants={colors
                                    .filter((c) => areasInPlacement.some((x) => x.colorID === c.colorID))
                                    .map((c) => {
                                        return { colorID: c.colorID, name: c.name };
                                    })}
                                layers={layersInPlacement}
                                onRemove={handleRemovePdf}
                                onButtonClick={handleUploadPdf}
                            />
                        )}

                        {currentMenuPage === MenuPages.InfoArea && selectedItemGuid && (
                            <InfoAreaMenu force={forceInfoAreaUpdate.toString()} key={forceInfoAreaUpdate.toString()} items={getInfoItems()} />
                        )}

                        {currentMenuPage === MenuPages.Settings && (
                            <SettingsMenu
                                widthCM={widthCM}
                                heightCM={heightCM}
                                dpi={dpi}
                                unit={unit}
                                name={name}
                                containsLayers={layersInPlacement.length > 0}
                                stacked={stacked}
                                onConfirm={(unit, widthCM, heightCM, dpi, name, stacked, removeLayers) => {
                                    setUnit(unit);
                                    setWidthCM(widthCM);
                                    setHeightCM(heightCM);
                                    setDpi(dpi);
                                    setName(name);
                                    setStacked(stacked);
                                    if (removeLayers) setLayersInPlacement([]);
                                }}
                            />
                        )}

                        {currentMenuPage === MenuPages.QrBarCode && <QrBarCodeMenu onConfirm={handleConfirmAddQrBarCode} />}
                    </PanelContent>

                    <PanelFooter>
                        <PrimaryButton
                            disabled={areasInPlacement.length == 0 || !subscription?.plan?.additionalFeatures.advancedPrintFilesBuilder || savingInProgress}
                            size='small'
                            onClick={() =>
                                onSaveClick({
                                    id: placement?.placementOutput?.id,
                                    name: name,
                                    dpi: dpi,
                                    width: widthCM,
                                    heigth: heightCM,
                                    areas: areasInPlacement,
                                    items: itemsInPlacement,
                                    layers: layersInPlacement,
                                    settings: settingsInPlacement
                                } as PlacementInput)
                            }
                        >
                            {T._('Save', 'Admin')}
                        </PrimaryButton>
                    </PanelFooter>
                </RigthPanelContainer>
            </AreaDesignerSidePanel>
        </AreaDesignerLayout>
    );
};

export default PlacementAreaDesigner;
